import { toJS } from 'mobx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DocumentType } from '../stores/i18n.store';
import { Languages } from '../types/lang.type';
import { useStoreCommon } from './useStore.hook';

export declare namespace UseTranslationType {
  type Props = {
    lang: Languages | null;
    t: (tag: string, replaces?: Record<string, string>) => string;
  };
}

export const useTranslation = (): UseTranslationType.Props => {
  const { I18NStore } = useStoreCommon();

  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [defaultDocument, setDefaultDocument] = useState<DocumentType>();

  const lang = I18NStore.lang;
  const dictionary = I18NStore.dictionary;

  const documentsFormatted = useMemo(() => {
    const documentsMap = [...documents, ...I18NStore.injectDocumentTemp].reduce(
      (acc, doc) => {
        const existingDoc = acc.get(doc.lang);
        acc.set(
          doc.lang,
          existingDoc
            ? {
                ...existingDoc,
                ...doc,
                _id: doc.lang,
                translations: {
                  ...existingDoc.translations,
                  ...doc.translations,
                },
              }
            : { ...doc, _id: doc.lang },
        );
        return acc;
      },
      new Map<string, DocumentType>(),
    );

    return Array.from(documentsMap.values());
  }, [I18NStore.injectDocumentTemp, documents]);

  const currentDocument = useMemo(() => {
    return documentsFormatted.find((document) => document.lang === lang);
  }, [documentsFormatted, lang]);

  useEffect(() => {
    if (!documents.length && dictionary && dictionary.documents) {
      setDocuments(dictionary.documents);
      const defaultDocument = documents.find(
        ({ lang: langInner }) => langInner === lang,
      );
      setDefaultDocument(defaultDocument);
    }
  }, [dictionary]);

  const t = useCallback(
    (tag: string, replaces?: Record<string, string>): string => {
      if (!tag) return '';
      let text = `${tag}`;
      // if (!currentDocument) return ''
      if (currentDocument?.translations?.[tag])
        text = currentDocument?.translations?.[tag];
      else if (defaultDocument?.translations?.[tag])
        text = '#' + defaultDocument?.translations?.[tag];
      else text = `##${tag}`;

      if (replaces) {
        // Handle replacements
        return Object.entries(replaces).reduce(
          (acc, [key, value]) => acc.replaceAll(key, value),
          text,
        );
      }

      return text;
    },
    [currentDocument],
  );

  return {
    lang: (currentDocument?.lang as Languages | undefined) || null,
    t,
  };
};
